// =====================================================================
// src/screens/v2/Cart.jsx
//
// V2 Cart + Checkout screen.
//
// Two sub-screens (toggled by internal state):
//   1. cart    — line items, delivery/pickup, notes, summary, Place Order
//   2. confirm — order placed confirmation with next steps
//
// V2_HANDOFF compliance:
//   - Aangan NEVER processes payment. Payment shared via in-app chat
//     by the business AFTER they confirm the order.
//   - Plain English. No card forms. No fees collected.
//
// In Phase B, MOCK cart state will be replaced by Firestore-backed cart
// per (userId, businessId) and orders will be written to the `orders`
// collection via a Cloud Function for atomicity.
//
// Props:
//   businessId        — which business this cart is for
//   onBack            — return to previous V2 screen (Profile)
//   onOpenChat        — open chat with business (placeholder)
//   onOpenOrderDetail — view the placed order detail (placeholder)
// =====================================================================

import React, { useState, useMemo, useEffect } from 'react';
import {
  ArrowLeft, MessageSquare, MapPin, Clock,
  Truck, Footprints, ShoppingCart, ArrowUpRight, X, ChevronRight, Check, Loader2, Plus
} from 'lucide-react';
import {
  collection, addDoc, doc, getDoc, setDoc, onSnapshot, serverTimestamp,
  query, where, limit, getDocs, documentId,
} from 'firebase/firestore';
import { db, auth } from '../../lib/firebase';
import { isBusinessActive, getSubscriptionState } from '../../lib/v2/isBusinessActive';
import {
  getBusiness,
  logoBgFromCategory,
  categoryLabelFromId,
  todayHoursStr,
} from '../../lib/v2/firestore';
// C7 — override-aware open status.
import { getEffectiveOpenStatus } from '../../lib/v2/openStatusOverride';
import { getCart, setCart as persistCart, clearCart } from '../../lib/v2/cart';
import './Cart.css';
import BusinessLogo from '../../components/BusinessLogo';

// ============================================================
// MOCK DATA
// ============================================================

// Per-business header info (minimal — just what cart needs)
// ============================================================
// HELPERS
// ============================================================

// Category → emoji for the cart's business logo. Same mapping as
// OrderDetail. Future refactor could lift this into derive.js.
const CATEGORY_EMOJI = {
  food_beverages: '🍯',
  apparel: '👗',
  services: '🔧',
  groceries: '🛒',
  electronics: '📱',
  health_beauty: '💄',
  home_decor: '🏡',
};
function emojiForCategory(cat) {
  return CATEGORY_EMOJI[cat] || '🏪';
}

// Placeholder header shown while the real business loads, or as a
// fallback if the doc is missing critical display fields. Disabling
// the Place Order button is governed separately by businessReady.
const GENERIC_HEADER = {
  name: 'Business',
  logo: '🏪', logoBg: 'linear-gradient(135deg, #B8893A 0%, #D6B576 100%)',
  open: false, openTimeStr: 'Loading…',
  pickupAddress: '—',
  pickupWindow: '—',
  deliveryAvailable: false, deliveryRadius: '—', deliveryFeeStr: '—', deliveryFee: 0,
  status: 'active',
};

// Cart starts empty in real flow — items are loaded from the business's
// products subcollection in useEffect. For Step 3.5a, we pre-populate
// with the first few real products so the order-placement pipeline can
// be tested end-to-end. Step 3.5c will replace this with an empty cart
// + "Add to Cart" wiring from BusinessProfile.
const INITIAL_ITEMS = [];

// The customer's delivery address now comes from /users/{uid}.deliveryAddress,
// entered by the customer and saved back for next time.
//
// It used to be this module-level constant:
//
//   const MOCK_DELIVERY_ADDRESS = 'A-204, Cherry Apartments, Sector 50, Noida — 201301';
//
// which was not merely displayed — it was written into orderDoc.deliveryAddress
// on EVERY delivery order, and MerchantApp renders that field verbatim. Every
// delivery order Aangan ever took told the merchant to deliver to a flat in
// Cherry Apartments. The comment beside it said "in Phase B, comes from user
// profile"; Phase B never came, and nothing failed loudly enough to notice.
//
// Address is free text and display-only: deliveryFee is a flat per-business
// amount (businessDoc.deliveryFee), never distance-derived, so editing the
// address cannot change the total. No map, no autocomplete, no radius check.

// Disclaimer dismissal persistence (per device, all businesses)
const DISCLAIMER_KEY = 'aangan_v2_cart_disclaimer_dismissed';

function getDisclaimerDismissed() {
  try { return localStorage.getItem(DISCLAIMER_KEY) === 'true'; } catch { return false; }
}
function setDisclaimerDismissed() {
  try { localStorage.setItem(DISCLAIMER_KEY, 'true'); } catch {}
}

// Order display number: AGN-YYYY-XXXXX
// Suffix uses A-Z + 2-9 (excludes I/O/0/1 to avoid visual confusion).
// 31^5 = ~28.6M combos — collision risk vanishingly small at our scale.
// Rule regex ^AGN-[0-9]{4}-[A-Z0-9]{5}$ accepts this.
function generateOrderId() {
  const year = new Date().getFullYear();
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let suffix = '';
  for (let i = 0; i < 5; i++) {
    suffix += chars[Math.floor(Math.random() * chars.length)];
  }
  return `AGN-${year}-${suffix}`;
}

// ============================================================
// COMPONENT
// ============================================================

export default function Cart({ businessId, onBack, onOpenChat, onOpenOrderDetail, onAddItems }) {
  // Sub-screen state
  const [subscreen, setSubscreen] = useState('cart');   // 'cart' | 'confirm'
  const [orderId, setOrderId] = useState(null);

  // Cart state
  const [items, setItems] = useState(INITIAL_ITEMS);
  const [mode, setMode] = useState('delivery');  // adjusted by effect if business doesn't deliver
  const [notes, setNotes] = useState('');
  const [disclaimerHidden, setDisclaimerHidden] = useState(() => getDisclaimerDismissed());

  // Toast
  const [toast, setToast] = useState(null);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  // ============================================================
  // FIRESTORE STATE — business header (Step 3.5c)
  // ============================================================
  // BUSINESS_HEADERS mock is gone. Real business doc fetched on
  // businessId change. GENERIC_HEADER is the placeholder during load.
  const [businessDoc, setBusinessDoc] = useState(null);
  const [loadingBusiness, setLoadingBusiness] = useState(true);
  const [businessError, setBusinessError] = useState(null);  // 'not_found' | 'not_transactable' | 'other'

  useEffect(() => {
    if (!businessId) {
      setLoadingBusiness(false);
      setBusinessError('not_found');
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingBusiness(true);
        setBusinessError(null);
        const b = await getBusiness(businessId);
        if (cancelled) return;
        if (!b) {
          setBusinessError('not_found');
          setBusinessDoc(null);
          return;
        }
        setBusinessDoc(b);
        // Y10 (Path C): cart is open to verified businesses and to
        // pending_verification businesses still in their trial window.
        // The server-side rule isTransactableBusiness already enforces
        // this for /orders writes; we mirror it client-side so users
        // aren't blocked at the cart screen before they can submit.
        // Suspended (and future trial-expired) businesses still hit the
        // not_transactable error path.
        if (b.status !== 'verified' && b.status !== 'pending_verification') {
          setBusinessError('not_transactable');
        }
      } catch (err) {
        console.error('[Cart] business load failed:', err);
        if (!cancelled) setBusinessError('other');
      } finally {
        if (!cancelled) setLoadingBusiness(false);
      }
    })();
    return () => { cancelled = true; };
  }, [businessId]);

  // ============================================================
  // FIRESTORE STATE (Step 3.5a)
  // ============================================================

  // Loading the business's products on cart open so we have real
  // product IDs + prices for the order write. Step 3.5c will replace
  // this with items added from BusinessProfile.
  const [loadingProducts, setLoadingProducts] = useState(true);

  // Order placement + status subscription state
  // ---- Delivery address (replaces MOCK_DELIVERY_ADDRESS) ----
  // deliveryAddress  — what will be written to the order
  // savedAddress     — what /users/{uid} already holds, so we only write back
  //                    when it actually changed
  // addressLoaded    — guards the one-time read; also lets the UI distinguish
  //                    "still loading" from "genuinely empty"
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [savedAddress, setSavedAddress] = useState('');
  const [addressLoaded, setAddressLoaded] = useState(false);
  const [editingAddress, setEditingAddress] = useState(false);

  const [placing, setPlacing] = useState(false);
  const [displayNumber, setDisplayNumber] = useState(null);
  const [orderStatus, setOrderStatus] = useState(null);   // 'placed' | 'confirmed' | 'cancelled'
  const [validationFailures, setValidationFailures] = useState(null);

  // ============================================================
  // EFFECT: Load real products for this business on mount.
  // Limited to 3 so cart isn't overwhelming for the test pipeline.
  // ============================================================
  // ============================================================
  // EFFECT: hydrate cart items from localStorage + Firestore (6.0d.1)
  //
  // Previously this effect auto-seeded the cart with the first 3
  // products from the business (demo mode). Now it reads the persisted
  // cart from lib/v2/cart (productId + qty), then fetches the matching
  // product docs from Firestore to hydrate name / price / image.
  //
  // If the stored cart is empty (user landed here from somewhere other
  // than "+ Add" on BusinessProfile, e.g. directly typed ?v2=cart&id=X),
  // items stays []. The UI shows the empty-cart state in that case.
  // ============================================================
  useEffect(() => {
    if (!businessId) {
      setLoadingProducts(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingProducts(true);
        const stored = getCart(businessId);
        if (stored.length === 0) {
          if (!cancelled) {
            setItems([]);
            setLoadingProducts(false);
          }
          return;
        }
        // Hydrate each productId via getDoc; Firestore `in` would also
        // work but we have getDoc cached behavior already and it's just
        // 1-5 reads per cart in practice.
        const ids = stored.map(s => s.productId);
        const q = query(
          collection(db, 'businesses', businessId, 'products'),
          where(documentId(), 'in', ids.slice(0, 30)),  // 'in' max = 30
        );
        const snap = await getDocs(q);
        if (cancelled) return;
        const productsById = {};
        snap.docs.forEach(d => {
          const data = d.data();
          productsById[d.id] = {
            id: d.id,
            name: data.name || d.id,
            price: data.price || 0,
            // Reuse the seeded icon/iconBg if present, fall back to a
            // food-themed gradient (same shape Cart originally used)
            image: data.icon || '🛍️',
            imageBg: data.iconBg || 'linear-gradient(135deg, #C45A3F 0%, #E8A57A 100%)',
            // Phase 2.3 C3 — product photo (null if no upload). Photo
            // takes precedence in the cart line render; emoji falls
            // back when null OR if the image fails to load.
            imageUrl: data.imageUrl || null,
            inStock: data.inStock !== false,
          };
        });
        // Preserve order from stored cart; drop entries whose product
        // no longer exists (deleted between add-to-cart and cart open).
        const hydrated = stored
          .map(s => productsById[s.productId]
            ? { ...productsById[s.productId], qty: s.qty }
            : null)
          .filter(Boolean);
        setItems(hydrated);
      } catch (err) {
        console.error('[Cart] cart load failed:', err);
        setToast('Could not load cart. Try again.');
      } finally {
        if (!cancelled) setLoadingProducts(false);
      }
    })();
    return () => { cancelled = true; };
  }, [businessId]);

  // Persist any items change back to localStorage so the cart state
  // survives a refresh / page navigation. We skip the very first render
  // when items is still []; otherwise the initial render before the
  // hydration effect resolves would wipe the saved cart.
  const [hasHydrated, setHasHydrated] = useState(false);
  useEffect(() => {
    if (!businessId) return;
    if (!hasHydrated) {
      // Mark hydrated once loading finishes (regardless of result).
      if (!loadingProducts) setHasHydrated(true);
      return;
    }
    persistCart(
      businessId,
      items.map(i => ({ productId: i.id, qty: i.qty }))
    );
  }, [items, businessId, hasHydrated, loadingProducts]);

  // ============================================================
  // EFFECT: Subscribe to the placed order's status. Lets the
  // confirm screen show "Verifying…" → "Confirmed ✓" / "Cancelled"
  // as the onOrderCreated Cloud Function processes it.
  // ============================================================
  useEffect(() => {
    if (subscreen !== 'confirm' || !orderId) return undefined;
    const orderRef = doc(db, 'orders', orderId);
    const unsub = onSnapshot(
      orderRef,
      (snap) => {
        if (!snap.exists()) return;
        const data = snap.data();
        setOrderStatus(data.status);
        if (data.status === 'cancelled') {
          setValidationFailures(data.validationFailures || null);
        }
      },
      (err) => {
        console.error('[Cart] order status subscription failed:', err);
      }
    );
    return () => unsub();
  }, [subscreen, orderId]);

  // ============================================================
  // DERIVED: business display object (Step 3.5c)
  // Builds the same shape the JSX below expects, sourced from the
  // real businessDoc + derive helpers. While loading or in an error
  // state, falls back to GENERIC_HEADER so the UI doesn't render
  // empty strings.
  // ============================================================
  const business = useMemo(() => {
    if (!businessDoc) return GENERIC_HEADER;
    const cat = businessDoc.category || '';
    // C7 — respect merchant manualOpenOverride; falls back to hours.
    const status = getEffectiveOpenStatus(businessDoc);
    const todayStr = todayHoursStr(businessDoc.hours || {});
    const locality = businessDoc.locality || businessDoc.city || '';
    const deliveryFee = businessDoc.deliveryFee || 0;
    return {
      name: businessDoc.name || 'Business',
      logo: emojiForCategory(cat),
      logoBg: logoBgFromCategory(cat),
      // Phase 2.3 C2.5 — uploaded logo URL passed through from the business doc.
      logoImageUrl: businessDoc.logoImageUrl || null,
      open: status?.open ?? false,
      openTimeStr: status?.openMeta || (status?.open ? 'Open' : 'Closed'),
      pickupAddress: businessDoc.address || [locality, businessDoc.city].filter(Boolean).join(', ') || '—',
      pickupWindow: todayStr ? `Today · ${todayStr}` : 'Hours unavailable',
      deliveryAvailable: businessDoc.deliveryAvailable !== false,
      deliveryRadius: businessDoc.deliveryRadius || '5 km',
      deliveryFee,
      deliveryFeeStr: deliveryFee > 0 ? `₹${deliveryFee} flat fee` : 'Free delivery',
    };
  }, [businessDoc]);

  // ============================================================
  // Can this business actually receive an order?
  // ============================================================
  //
  // Mirror BOTH clauses of the /orders create rule. Getting this wrong hands
  // the customer a live "Place Order" button and then a raw permission-denied
  // from Firestore, which is what happened before this fix:
  //
  //   allow create: ... && isTransactableBusiness(bizId)   // the status enum
  //                 ... && isBusinessActive(bizId)         // paid or in trial
  //
  // Previously Cart derived `isLapsed` from `status === 'suspended'` — a third
  // notion matching neither rule. Worse, a suspended business already trips the
  // not_transactable error screen above and returns early, so isLapsed could
  // never be true and this whole "not taking orders" UI was unreachable dead
  // code. It is exactly the right UI; it was wired to the wrong signal.
  //
  // Two states the old check let through, both ending in permission-denied:
  //   1. pending_verification whose free trial has expired
  //   2. verified but Aangan Connect inactive (never paid, or window lapsed)
  const subState = getSubscriptionState(businessDoc);

  // isTransactableBusiness, client-side: verified, or pending while still
  // inside the free trial (the trial is the grace window in which a new
  // business may trade while we check its documents).
  const isTransactable = businessDoc
    ? (businessDoc.status === 'verified'
        || (businessDoc.status === 'pending_verification' && subState === 'active-trial'))
    : false;

  // isBusinessActive, client-side: the same helper BusinessProfile, MerchantApp
  // and the directory use, so all four surfaces finally agree.
  const isActive = isBusinessActive(businessDoc);

  const isLapsed = !!businessDoc && (!isTransactable || !isActive);

  // Loud rather than silent: if getBusiness() ever stops returning the
  // activation fields, every business would look lapsed and orders would
  // vanish. Say so in the console instead of failing quietly.
  useEffect(() => {
    if (!businessDoc) return;
    const hasAnyActivationField = 'activated' in businessDoc
      || 'activationValidTill' in businessDoc
      || 'trialStartedAt' in businessDoc;
    if (!hasAnyActivationField) {
      console.warn(
        '[Cart] business doc has no activation fields — every business will '
        + 'appear inactive. Check getBusiness() field mapping.',
        businessDoc.id,
      );
    }
  }, [businessDoc]);

  // If the loaded business doesn't support delivery, switch the user's
  // selection to pickup. Runs once on businessDoc transition.
  useEffect(() => {
    if (businessDoc && businessDoc.deliveryAvailable === false && mode === 'delivery') {
      setMode('pickup');
    }
    // We intentionally only react to businessDoc, not mode, so the
    // user can still flip back to delivery if they want (and if the
    // business supports it, the UI will allow it).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDoc]);

  // Computed totals
  const subtotal = useMemo(
    () => items.reduce((sum, i) => sum + i.price * i.qty, 0),
    [items]
  );
  const itemCount = useMemo(
    () => items.reduce((sum, i) => sum + i.qty, 0),
    [items]
  );
  // Read the saved address once, and only if delivery is actually on the table.
  // Firestore cost: 1 read, never repeated (addressLoaded latches).
  useEffect(() => {
    if (addressLoaded) return;
    if (mode !== 'delivery') return;
    const uid = auth.currentUser?.uid;
    if (!uid) return;

    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'users', uid));
        const existing = (snap.exists() && snap.data().deliveryAddress) || '';
        if (cancelled) return;
        setDeliveryAddress(existing);
        setSavedAddress(existing);
        // No address on file → open straight into the editor rather than
        // showing an empty "Deliver to" line the customer has to discover.
        setEditingAddress(!existing);
      } catch (err) {
        console.warn('[Cart] could not read saved address:', err?.code || err);
        if (!cancelled) setEditingAddress(true);
      } finally {
        if (!cancelled) setAddressLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [mode, addressLoaded]);

  const deliveryFee = mode === 'delivery' && business.deliveryAvailable ? business.deliveryFee : 0;

  // A delivery order without an address is exactly how MOCK_DELIVERY_ADDRESS
  // survived: nothing ever forced the question. Block placement instead.
  const needsAddress = mode === 'delivery'
    && business.deliveryAvailable
    && !deliveryAddress.trim();
  const total = subtotal + deliveryFee;

  // Cart operations
  function changeQty(itemId, delta) {
    setItems(prev => prev.map(i =>
      i.id === itemId ? { ...i, qty: Math.max(0, i.qty + delta) } : i
    ).filter(i => i.qty > 0));
  }
  function removeItem(itemId) {
    setItems(prev => prev.filter(i => i.id !== itemId));
  }
  function dismissDisclaimer() {
    setDisclaimerHidden(true);
    setDisclaimerDismissed();
  }

  async function placeOrder() {
    if (placing) return;  // ignore double-clicks
    if (items.length === 0) {
      setToast('Add at least one item to place an order');
      return;
    }
    if (isLapsed) {
      // The old body here was `setToast("Sent — they'll be notified you're
      // interested")` — and nothing was ever sent. No write, no
      // openSubscribers entry, no notification. It went unnoticed because
      // isLapsed was derived from `status === 'suspended'`, which the
      // not_transactable error screen already caught, so this branch was
      // unreachable. Now that the flag points at the real signal, the copy
      // has to be true.
      setToast(`${business.name} isn't accepting orders right now`);
      return;
    }
    if (!business.open) {
      setToast(`${business.name} is currently closed. Try again when they're open.`);
      return;
    }

    const uid = auth.currentUser?.uid;
    if (!uid) {
      setToast('Please sign in to place an order');
      return;
    }

    setPlacing(true);
    try {
      // Build the order doc EXACTLY matching the locked schema (Step 4a):
      //   customerId, businessId, items[{productId,name,price,quantity}],
      //   subtotal, deliveryFee, total, mode, status='placed',
      //   displayNumber AGN-YYYY-XXXXX, placedAt, optional notes/deliveryAddress.
      // The onOrderCreated Cloud Function will re-validate prices against
      // /businesses/{bid}/products and flip status to 'confirmed' or 'cancelled'.
      const newDisplayNumber = generateOrderId();
      // Phase 2.3 C3 — snapshot the imageUrl into each order item so
      // OrderDetail can render product photos without re-fetching the
      // product docs (and so the order keeps a stable photo reference
      // even if the merchant later changes the product's image).
      const orderItems = items.map(it => ({
        productId: it.id,
        name: it.name,
        price: it.price,
        quantity: it.qty,
        ...(it.imageUrl ? { imageUrl: it.imageUrl } : {}),
      }));

      const orderDoc = {
        customerId: uid,
        businessId,
        items: orderItems,
        subtotal,
        deliveryFee,
        total,
        mode,
        status: 'placed',
        displayNumber: newDisplayNumber,
        placedAt: serverTimestamp(),
      };
      if (notes && notes.trim()) {
        orderDoc.notes = notes.trim();
      }
      if (mode === 'delivery') {
        orderDoc.deliveryAddress = deliveryAddress.trim();
      }

      const ref = await addDoc(collection(db, 'orders'), orderDoc);

      // Remember the address for next time. Best-effort and deliberately AFTER
      // the order write: a failure here must never lose an order the merchant
      // has already been notified about. Only writes when it actually changed,
      // so a repeat customer costs 0 extra writes.
      //
      // No rules change needed: /users update is isMe(uid) with a blocklist
      // (superAdmin, isPlatformAdmin, photo counters), not a keys().hasOnly()
      // allowlist, so a new deliveryAddress field passes.
      if (mode === 'delivery') {
        const trimmed = deliveryAddress.trim();
        if (trimmed && trimmed !== savedAddress) {
          try {
            await setDoc(doc(db, 'users', uid), { deliveryAddress: trimmed }, { merge: true });
            setSavedAddress(trimmed);
          } catch (err) {
            console.warn('[Cart] could not save address to profile:', err?.code || err);
          }
        }
      }

      setOrderId(ref.id);
      setDisplayNumber(newDisplayNumber);
      setOrderStatus('placed');           // function will flip this within ~1-5s
      setValidationFailures(null);
      // 6.0d.1: clear the persisted cart now that the order is in.
      // Items state is left intact so the confirmation screen can still
      // show what was ordered.
      clearCart(businessId);
      setSubscreen('confirm');
      window.scrollTo(0, 0);
    } catch (err) {
      console.error('[Cart] placeOrder failed:', err);
      setToast(`Failed to place order: ${err.code || err.message || 'unknown'}`);
    } finally {
      setPlacing(false);
    }
  }

  // ============================================================
  // EARLY RETURN — business error state (Step 3.5c)
  // If the business doc can't be loaded or isn't verified, block the
  // entire cart flow. We don't want users filling a cart for a business
  // they can't actually order from.
  //
  // Note: we deliberately don't show a full-screen loading state for
  // the business — the cart layout uses GENERIC_HEADER as a placeholder
  // during load so the UI doesn't flash empty.
  // ============================================================
  if (!loadingBusiness && businessError && subscreen === 'cart') {
    let title = 'Business unavailable';
    let message = 'We couldn\'t load this business. Please try again later.';
    if (businessError === 'not_found') {
      title = 'Business not found';
      message = 'This business may have been removed, or the link is incorrect.';
    } else if (businessError === 'not_transactable') {
      title = 'Business not accepting orders';
      message = 'This business isn\'t available for ordering right now. Please check back later.';
    }
    return (
      <div className="v2-cart">
        <div className="v2c-top-bar">
          <button className="v2c-back-btn" onClick={onBack} aria-label="Back">
            <ArrowLeft size={20} />
          </button>
          <div className="v2c-top-info">
            <div className="v2c-top-title">{title}</div>
          </div>
        </div>
        <div style={{ padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🏪</div>
          <h2 style={{ marginBottom: 12 }}>{title}</h2>
          <p style={{ color: 'var(--text-muted, #6E6E78)' }}>{message}</p>
        </div>
      </div>
    );
  }

  // ============================================================
  // RENDER — CART SUB-SCREEN
  // ============================================================
  if (subscreen === 'cart') {
    return (
      <div className="v2-cart">
        {/* Top bar */}
        <div className="v2c-top-bar">
          <button className="v2c-back-btn" onClick={onBack} aria-label="Back">
            <ArrowLeft size={20} />
          </button>
          <BusinessLogo
            className="v2c-top-logo"
            imageUrl={business.logoImageUrl}
            emoji={business.logo}
            bg={business.logoBg}
          />
          <div className="v2c-top-info">
            <div className="v2c-top-title">{business.name}</div>
            <div className="v2c-top-sub">
              Your order ·{' '}
              <span className={business.open ? 'v2c-status-open' : 'v2c-status-closed'}>
                {business.open ? '🟢' : '🔴'} {business.openTimeStr}
              </span>
            </div>
          </div>
          <button
            className="v2c-icon-btn"
            onClick={() => orderId && onOpenChat?.(orderId)}
            aria-label="Messages"
            disabled={!orderId}
          >
            <MessageSquare size={18} />
          </button>
          {/* Placeholder sweep: the "More options" 3-dots opened nothing but a
              "coming soon" toast. A control that does nothing teaches people the
              app is broken. Its natural contents (Report this order, Block) are
              contextual reporting — a planned session that gates V2 on Android.
              Removed until there is something real to put behind it. */}
        </div>

        {/* Scrollable content */}
        <div className="v2c-content">

          {/* Disclaimer */}
          {!disclaimerHidden && (
            <div className="v2c-disclaimer">
              <span className="v2c-disclaimer-icon">ℹ️</span>
              <div className="v2c-disclaimer-body">
                <div className="v2c-disclaimer-title">About Aangan's role</div>
                <div className="v2c-disclaimer-text">
                  Aangan connects you with this business. The business
                  handles order preparation, delivery, payment, and refunds.
                  For order issues, use in-app chat. For platform issues,
                  email <a href="mailto:aangansupport@gmail.com">aangansupport@gmail.com</a>.
                </div>
                <button
                  className="v2c-disclaimer-dismiss"
                  onClick={dismissDisclaimer}
                >
                  Got it
                </button>
              </div>
            </div>
          )}

          {/* Not-accepting-orders banner. Shown when the business fails either
              clause of the /orders create rule: its Aangan Connect window has
              lapsed, or it is still pending verification and its free trial
              has run out. Deliberately does NOT promise a notification — the
              old copy said "we'll notify you when they're back online" and no
              such record was ever written. */}
          {isLapsed && (
            <div className="v2c-banner v2c-banner-lapsed">
              <span style={{ fontSize: 16 }}>🔔</span>
              <span>
                <b>{business.name} isn't accepting orders right now.</b>{' '}
                Your items are saved. Try again later, or message them from
                their profile.
              </span>
            </div>
          )}

          {/* Closed business banner (not lapsed, just outside hours) */}
          {!isLapsed && !business.open && (
            <div className="v2c-banner v2c-banner-closed">
              <span style={{ fontSize: 16 }}>🌙</span>
              <span>
                <b>Closed right now</b> · You can still build the order;
                placing it will be available when they reopen.
              </span>
            </div>
          )}

          {/* Items section */}
          <div className="v2c-section">
            <div className="v2c-section-label">
              Your items
              <span className="v2c-section-count">
                {itemCount} {itemCount === 1 ? 'item' : 'items'}
              </span>
            </div>

            {items.length === 0 ? (
              <div className="v2c-empty-cart">
                <div style={{ fontSize: 36, marginBottom: 6 }}>🛒</div>
                <div className="v2c-empty-cart-title">Your cart is empty</div>
                <div className="v2c-empty-cart-sub">
                  Tap <b>Add items</b> below to browse products.
                </div>
              </div>
            ) : (
              <div className="v2c-items-list">
                {items.map(item => (
                  <div key={item.id} className="v2c-item">
                    <div
                      className="v2c-item-image"
                      style={{
                        background: item.imageUrl ? 'transparent' : item.imageBg,
                        overflow: 'hidden',
                      }}
                    >
                      {item.imageUrl ? (
                        <img
                          src={item.imageUrl}
                          alt=""
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: 'inherit',
                            display: 'block',
                          }}
                        />
                      ) : (
                        item.image
                      )}
                    </div>
                    <div className="v2c-item-body">
                      <div className="v2c-item-top">
                        <div className="v2c-item-name">{item.name}</div>
                        <button
                          className="v2c-item-remove"
                          onClick={() => removeItem(item.id)}
                          aria-label="Remove"
                        >
                          <X size={14} />
                        </button>
                      </div>
                      <div className="v2c-item-bottom">
                        <div className="v2c-qty-stepper">
                          <button
                            className="v2c-qty-btn"
                            onClick={() => changeQty(item.id, -1)}
                            aria-label="Decrease quantity"
                          >−</button>
                          <span className="v2c-qty-value">{item.qty}</span>
                          <button
                            className="v2c-qty-btn"
                            onClick={() => changeQty(item.id, 1)}
                            aria-label="Increase quantity"
                          >+</button>
                        </div>
                        <div className="v2c-item-price">
                          ₹{(item.price * item.qty).toLocaleString('en-IN')}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Delivery / Pickup */}
          <div className="v2c-section">
            <div className="v2c-section-label">How would you like to receive it?</div>

            <div className="v2c-delivery-tabs">
              {business.deliveryAvailable && (
                <button
                  className={`v2c-delivery-tab ${mode === 'delivery' ? 'active' : ''}`}
                  onClick={() => setMode('delivery')}
                >
                  <Truck size={14} /> Delivery
                </button>
              )}
              <button
                className={`v2c-delivery-tab ${mode === 'pickup' ? 'active' : ''}`}
                onClick={() => setMode('pickup')}
              >
                <Footprints size={14} /> Pickup
              </button>
            </div>

            {mode === 'delivery' && business.deliveryAvailable && (
              <div className="v2c-delivery-card">
                <div className="v2c-delivery-row">
                  <div className="v2c-delivery-icon"><MapPin size={14} /></div>
                  <div className="v2c-delivery-body">
                    <div className="v2c-delivery-label">Deliver to</div>

                    {!addressLoaded ? (
                      <div className="v2c-delivery-value" style={{ opacity: 0.6 }}>Loading…</div>
                    ) : editingAddress ? (
                      <>
                        <textarea
                          className="v2c-delivery-input"
                          value={deliveryAddress}
                          onChange={(e) => setDeliveryAddress(e.target.value)}
                          placeholder="Flat / house, street, landmark, area, pincode"
                          rows={3}
                          maxLength={300}
                          style={{
                            width: '100%',
                            marginTop: 4,
                            padding: '8px 10px',
                            borderRadius: 8,
                            border: '1px solid var(--border)',
                            background: 'var(--bg)',
                            color: 'var(--ink)',
                            fontFamily: 'inherit',
                            fontSize: 13,
                            lineHeight: 1.45,
                            resize: 'none',
                            outline: 'none',
                          }}
                        />
                        <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                          <button
                            className="v2c-delivery-action"
                            disabled={!deliveryAddress.trim()}
                            style={{ opacity: deliveryAddress.trim() ? 1 : 0.5 }}
                            onClick={() => setEditingAddress(false)}
                          >
                            <Check size={11} /> Done
                          </button>
                          {savedAddress && (
                            <button
                              className="v2c-delivery-action"
                              onClick={() => {
                                setDeliveryAddress(savedAddress);
                                setEditingAddress(false);
                              }}
                            >
                              Cancel
                            </button>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="v2c-delivery-value">{deliveryAddress}</div>
                        <button
                          className="v2c-delivery-action"
                          onClick={() => setEditingAddress(true)}
                        >
                          Change address <ChevronRight size={11} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <div className="v2c-delivery-row">
                  <div className="v2c-delivery-icon"><Truck size={14} /></div>
                  <div className="v2c-delivery-body">
                    <div className="v2c-delivery-label">Delivery details</div>
                    <div className="v2c-delivery-value">
                      {business.name} delivers within {business.deliveryRadius} · {business.deliveryFeeStr}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {mode === 'pickup' && (
              <div className="v2c-delivery-card">
                <div className="v2c-delivery-row">
                  <div className="v2c-delivery-icon">🏪</div>
                  <div className="v2c-delivery-body">
                    <div className="v2c-delivery-label">Pickup from</div>
                    <div className="v2c-delivery-value">
                      <b>{business.name}</b> · {business.pickupAddress}
                    </div>
                    <button
                      className="v2c-delivery-action"
                      onClick={() => setToast('Opens Google Maps')}
                    >
                      Get directions <ArrowUpRight size={11} />
                    </button>
                  </div>
                </div>
                <div className="v2c-delivery-row">
                  <div className="v2c-delivery-icon"><Clock size={14} /></div>
                  <div className="v2c-delivery-body">
                    <div className="v2c-delivery-label">Pickup window</div>
                    <div className="v2c-delivery-value">
                      {business.pickupWindow}
                      <div className="v2c-delivery-hint">
                        Business will confirm a time when they accept your order.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="v2c-section">
            <div className="v2c-section-label">
              Notes for the business{' '}
              <span className="v2c-section-count v2c-section-count-soft">optional</span>
            </div>
            <textarea
              className="v2c-notes-input"
              placeholder="e.g. Less sugar in sweets, deliver after 6 PM, leave with watchman..."
              maxLength={300}
              value={notes}
              onChange={e => setNotes(e.target.value)}
            />
            <div className="v2c-notes-help">
              Anything the business should know about your order.{' '}
              {notes.length} / 300
            </div>
          </div>

          {/* Summary */}
          <div className="v2c-section">
            <div className="v2c-section-label">Order summary</div>
            <div className="v2c-summary">
              <div className="v2c-summary-row">
                <span>Subtotal ({itemCount} {itemCount === 1 ? 'item' : 'items'})</span>
                <span className="v2c-summary-value">₹{subtotal.toLocaleString('en-IN')}</span>
              </div>
              {mode === 'delivery' && business.deliveryAvailable && (
                <div className="v2c-summary-row">
                  <span>Delivery fee · Within {business.deliveryRadius}</span>
                  <span className="v2c-summary-value">₹{deliveryFee}</span>
                </div>
              )}
              {mode === 'pickup' && (
                <div className="v2c-summary-row">
                  <span>Pickup</span>
                  <span className="v2c-summary-value" style={{ color: 'var(--success)' }}>Free</span>
                </div>
              )}
              <div className="v2c-summary-divider" />
              <div className="v2c-summary-row v2c-summary-total">
                <span>Total</span>
                <span className="v2c-summary-value">₹{total.toLocaleString('en-IN')}</span>
              </div>
            </div>

            {/* Payment info */}
            <div className="v2c-payment-info">
              <span style={{ fontSize: 16 }}>💬</span>
              <span>
                The business will share <b>payment details (UPI / cash / link)</b> via
                in-app chat after they confirm your order. Aangan doesn't process payments.
              </span>
            </div>
          </div>
        </div>

        {/* Sticky bottom CTA */}
        <div className="v2c-bottom-cta">
          {items.length === 0 ? (
            // Path Z polish: empty cart shows an active 'Add items'
            // CTA that routes back to the business profile (where the
            // product list lives). Replaces the previous greyed-out
            // 'Place Order ₹0' + 'Go back to the business profile to
            // add items' helper text, which was easy to miss.
            <button
              className="v2c-place-btn"
              onClick={() => onAddItems?.() || setToast('Cannot navigate to products right now')}
            >
              <span className="v2c-place-btn-left">
                <Plus size={18} />
                <span>Add items</span>
              </span>
            </button>
          ) : (
            <button
              className={`v2c-place-btn ${((!business.open && !isLapsed) || placing || needsAddress || isLapsed) ? 'disabled' : ''}`}
              onClick={placeOrder}
              disabled={placing || needsAddress || isLapsed}
            >
              <span className="v2c-place-btn-left">
                {placing ? <Loader2 size={18} className="v2c-spinner" /> : <ShoppingCart size={18} />}
                <span>
                  {placing
                    ? 'Placing order…'
                    : isLapsed
                      ? 'Not accepting orders'
                      : !business.open
                        ? 'Closed right now'
                        : needsAddress
                          ? 'Add a delivery address'
                          : 'Place Order'}
                </span>
              </span>
              {!isLapsed && business.open && !placing && (
                <span className="v2c-place-btn-total">
                  ₹{total.toLocaleString('en-IN')}
                </span>
              )}
            </button>
          )}
        </div>

        {/* Toast */}
        {toast && <div className="v2c-toast">{toast}</div>}
      </div>
    );
  }

  // ============================================================
  // RENDER — CONFIRMATION SUB-SCREEN
  // Status-aware: shows Verifying… while the onOrderCreated function
  // runs, then Confirmed ✓ or Cancelled with failure details.
  // ============================================================
  const isVerifying = orderStatus === 'placed' || orderStatus === null;
  const isConfirmed = orderStatus === 'confirmed';
  // Pre-resolve the chat handler (see the note on the button below).
  const handleOpenChat = onOpenChat
    ? () => onOpenChat()
    : () => setToast('Chat is unavailable for this order');

  const isCancelled = orderStatus === 'cancelled';

  let statusIcon;
  let statusIconClass = 'v2c-confirm-check';
  let statusTitle;
  let statusSubtitle;

  if (isVerifying) {
    statusIcon = <Loader2 size={48} strokeWidth={2.5} className="v2c-spinner" />;
    statusIconClass = 'v2c-confirm-check verifying';
    statusTitle = 'Verifying your order…';
    statusSubtitle = `We're double-checking prices and availability with ${business.name}.`;
  } else if (isConfirmed) {
    statusIcon = <Check size={48} strokeWidth={2.5} />;
    statusTitle = 'Order confirmed!';
    statusSubtitle = `We've sent your order to ${business.name}`;
  } else if (isCancelled) {
    statusIcon = <X size={48} strokeWidth={2.5} />;
    statusIconClass = 'v2c-confirm-check cancelled';
    statusTitle = 'Order cancelled';
    statusSubtitle = 'There was an issue verifying your order. See details below.';
  }

  return (
    <div className="v2-cart">
      <div className="v2c-top-bar">
        <button className="v2c-back-btn" onClick={onBack} aria-label="Back">
          <ArrowLeft size={20} />
        </button>
        <div className="v2c-top-info">
          <div className="v2c-top-title">
            {isVerifying ? 'Verifying order' : isConfirmed ? 'Order placed' : 'Order cancelled'}
          </div>
          <div className="v2c-top-sub">
            {isVerifying
              ? 'Hold on a moment…'
              : isConfirmed
                ? "We've notified the business"
                : 'See details below'}
          </div>
        </div>
        <button
          className="v2c-icon-btn"
          onClick={() => orderId && onOpenChat?.(orderId)}
          aria-label="Messages"
          disabled={!orderId}
        >
          <MessageSquare size={18} />
        </button>
      </div>

      <div className="v2c-confirm">
        <div className={statusIconClass}>
          {statusIcon}
        </div>

        <h1 className="v2c-confirm-title">{statusTitle}</h1>
        <p className="v2c-confirm-subtitle">
          {statusSubtitle}
        </p>
        <div className="v2c-confirm-order-id">Order #{displayNumber || orderId}</div>

        {/* Validation failure details (only when cancelled by the function) */}
        {isCancelled && validationFailures && validationFailures.length > 0 && (
          <div className="v2c-confirm-card" style={{ borderColor: 'var(--danger, #C45A3F)' }}>
            <div className="v2c-confirm-next-steps">
              <b>What went wrong:</b>
              <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                {validationFailures.map((f, i) => (
                  <li key={i}>
                    {f.reason === 'price_mismatch' && (
                      <>Price for <b>{f.productId}</b> changed from ₹{f.submittedPrice} to ₹{f.actualPrice}.</>
                    )}
                    {f.reason === 'product_not_found' && (
                      <>Product <b>{f.productId}</b> is no longer available.</>
                    )}
                    {f.reason === 'out_of_stock' && (
                      <>Product <b>{f.productId}</b> is out of stock.</>
                    )}
                    {!['price_mismatch','product_not_found','out_of_stock'].includes(f.reason) && (
                      <>{f.reason}{f.productId ? ` (${f.productId})` : ''}</>
                    )}
                  </li>
                ))}
              </ul>
              <p style={{ marginTop: 12 }}>
                Please go back, refresh your cart, and try again.
              </p>
            </div>
          </div>
        )}

        <div className="v2c-confirm-card">
          <div className="v2c-confirm-biz-row">
            <BusinessLogo
              className="v2c-confirm-biz-logo"
              imageUrl={business.logoImageUrl}
              emoji={business.logo}
              bg={business.logoBg}
            />
            <div>
              <div className="v2c-confirm-biz-name">{business.name}</div>
              <div className="v2c-confirm-biz-meta">
                {itemCount} {itemCount === 1 ? 'item' : 'items'} · ₹{total.toLocaleString('en-IN')} · {mode === 'delivery' ? 'Delivery' : 'Pickup'}
              </div>
            </div>
          </div>
          {!isCancelled && (
            <div className="v2c-confirm-next-steps">
              <b>What happens next:</b>
              <ol>
                <li>The business will <b>confirm</b> your order via in-app chat.</li>
                <li>They'll share <b>payment details</b> (UPI / cash / link).</li>
                <li>You pay them directly. We don't process payments.</li>
                <li>They prepare and deliver / hand over your order.</li>
              </ol>
            </div>
          )}
        </div>

        <div className="v2c-confirm-actions">
          {!isCancelled && (
            <button
              className="v2c-confirm-btn primary"
              /* `onOpenChat?.() || setToast(...)` fired the toast even when the
                 prop WAS provided: onOpenChat() returns undefined, so `||`
                 always evaluated the right side and a "coming soon" toast
                 appeared on top of the chat that had just opened. ProfileTabV2
                 documents this exact trap and pre-resolves the handler instead. */
              onClick={handleOpenChat}
            >
              <MessageSquare size={16} /> Chat with {business.name}
            </button>
          )}
          <button
            className="v2c-confirm-btn secondary"
            onClick={() => onOpenOrderDetail?.(orderId) || setToast('Order Detail screen coming next')}
          >
            View order details
          </button>
          {isCancelled && (
            <button
              className="v2c-confirm-btn primary"
              onClick={onBack}
            >
              Back to cart
            </button>
          )}
        </div>

        <p className="v2c-confirm-footer">
          For order issues, chat with the business directly.<br/>
          For platform issues, email{' '}
          <a href="mailto:aangansupport@gmail.com">aangansupport@gmail.com</a>.
        </p>
      </div>

      {toast && <div className="v2c-toast">{toast}</div>}
    </div>
  );
}
